import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, FileText, History, MessageSquare, CheckCircle2, XCircle, Send, Upload, Star } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { StatusBadge } from "@/components/StatusBadge";
import { RatingForm, RatingDisplay } from "@/components/RatingForm";
import { api, formatDateTimeBR, ROLES } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";

export default function DocumentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [doc, setDoc] = useState(null);
  const [comment, setComment] = useState("");
  const [freeComment, setFreeComment] = useState("");
  const [versionForm, setVersionForm] = useState({ file_name: "", description: "" });
  const [verOpen, setVerOpen] = useState(false);
  const [ratingOpen, setRatingOpen] = useState(false);

  const refresh = () =>
    api.get(`/documents/${id}`).then(({ data }) => setDoc(data));

  useEffect(() => { refresh(); }, [id]);

  // M21 (2026-06-17): "Visualizar" versão do documento — chama backend com
  // auth via axios (retorna blob), cria URL de blob e abre em nova aba
  // (PDF/imagem) ou faz download (docx/outros).
  const viewVersion = async (version) => {
    if (!version || !version.file_url) {
      toast.error("Esta versão não possui arquivo anexado.");
      return;
    }
    try {
      const fileUrl = version.file_url.startsWith("/api/")
        ? version.file_url.slice(4) // remove '/api'
        : version.file_url;
      const { data, headers } = await api.get(fileUrl, { responseType: "blob" });
      const contentType = headers["content-type"] || "application/octet-stream";
      const blob = new Blob([data], { type: contentType });
      const blobUrl = window.URL.createObjectURL(blob);

      const filename = version.file_name || "documento";
      const ext = (filename.split(".").pop() || "").toLowerCase();
      const openableInline = ["pdf", "png", "jpg", "jpeg", "gif", "webp", "svg", "txt"];

      if (openableInline.includes(ext) || contentType.startsWith("image/") || contentType === "application/pdf") {
        // Abre em nova aba
        window.open(blobUrl, "_blank", "noopener,noreferrer");
      } else {
        // Force download
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      // Libera memória — pequeno delay para dar tempo do browser processar
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60_000);
    } catch (err) {
      const status = err.response?.status;
      if (status === 403) {
        toast.error("Você não tem permissão para acessar este arquivo.");
      } else if (status === 404) {
        toast.error("Arquivo não encontrado. Verifique se ele foi enviado corretamente.");
      } else {
        toast.error("Não foi possível abrir este documento. Tente novamente ou verifique se o arquivo foi enviado corretamente.");
      }
    }
  };

  if (!doc) return <Skeleton className="h-96" />;

  const action = async (path, requireComment = false) => {
    if (requireComment && !comment.trim()) {
      toast.error("Parecer/comentário é obrigatório para esta ação.");
      return;    }
    try {
      await api.post(`/documents/${id}/${path}`, { comment });
      toast.success("Ação registrada.");
      setComment("");
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro na operação.");
    }
  };

  const submitFreeComment = async () => {
    if (!freeComment.trim()) return toast.error("Comentário vazio.");
    try {
      await api.post(`/documents/${id}/comment`, { comment: freeComment });
      toast.success("Comentário registrado.");
      setFreeComment("");
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro.");
    }
  };

  const uploadVersion = async () => {
    try {
      await api.post(`/documents/${id}/new-version`, {
        contract_id: doc.contract_id,
        title: doc.title,
        file_name: versionForm.file_name || `documento_v${doc.current_version + 1}.pdf`,
        description: versionForm.description,
      });
      toast.success("Nova versão registrada (status: rascunho).");
      setVerOpen(false);
      setVersionForm({ file_name: "", description: "" });
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao subir versão.");
    }
  };

  const isConsultor = user.role === ROLES.CONSULTOR;
  const isJuridico = user.role === ROLES.JURIDICO;
  const isDiretora = user.role === ROLES.DIRETORA;
  const isSecretaria = user.role === ROLES.SECRETARIA;

  const canSubmitLegal = (isConsultor || isSecretaria) && (doc.status === "draft" || doc.status === "legal_changes_requested" || doc.status === "director_changes_requested");
  const canLegalAct = isJuridico && doc.status === "pending_legal";
  const canDirectorAct = isDiretora && doc.status === "pending_director";
  const canPublish = (isDiretora || isSecretaria) && doc.status === "approved";
  const canNewVersion = (isConsultor || isSecretaria) && doc.status !== "published";

  return (
    <div className="space-y-6">
      <button onClick={() => navigate(-1)} className="inline-flex items-center text-sm text-slate-600 hover:text-slate-900">
        <ArrowLeft className="w-4 h-4 mr-1" /> Voltar
      </button>

      <Card className="card-soft">
        <CardContent className="p-6">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <FileText className="w-5 h-5 text-slate-500" />
                <h1 className="text-xl font-semibold text-slate-900">{doc.title}</h1>
                <StatusBadge status={doc.status} />
                <span className="text-xs text-slate-500">v{doc.current_version}</span>
              </div>
              {doc.description && <p className="mt-2 text-sm text-slate-600 max-w-2xl">{doc.description}</p>}
              <div className="text-xs text-slate-500 mt-2">
                Criado por {doc.created_by_name} em {formatDateTimeBR(doc.created_at)}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Versions */}
      <Card className="card-soft">
        <CardHeader className="flex flex-row justify-between items-center">
          <CardTitle className="text-base flex items-center gap-2"><History className="w-4 h-4" /> Versões</CardTitle>
          {canNewVersion && (
            <Dialog open={verOpen} onOpenChange={setVerOpen}>
              <DialogTrigger asChild>
                <Button size="sm" variant="outline" data-testid="new-version-button">
                  <Upload className="w-4 h-4 mr-2" /> Subir nova versão
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Nova versão</DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label>Nome do arquivo</Label>
                    <Input value={versionForm.file_name}
                      onChange={(e) => setVersionForm({ ...versionForm, file_name: e.target.value })}
                      placeholder={`documento_v${doc.current_version + 1}.pdf`} />
                  </div>
                  <div>
                    <Label>Notas da versão</Label>
                    <Textarea rows={3}
                      value={versionForm.description}
                      onChange={(e) => setVersionForm({ ...versionForm, description: e.target.value })} />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setVerOpen(false)}>Cancelar</Button>
                  <Button onClick={uploadVersion} className="bg-midnight hover:bg-ink text-white">Registrar</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {doc.versions.slice().reverse().map((v) => (
              <div key={v.version} className="p-3 border border-slate-200 rounded-md flex items-center justify-between text-sm">
                <div>
                  <span className="font-medium">v{v.version}</span> — {v.file_name}
                  <span className="text-xs text-slate-500 ml-2">
                    por {v.uploaded_by_name} em {formatDateTimeBR(v.uploaded_at)}
                  </span>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-midnight hover:bg-midnight/10"
                  onClick={() => viewVersion(v)}
                  disabled={!v.file_url}
                  data-testid={`view-version-${v.version}`}
                >
                  Visualizar
                </Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      {(canSubmitLegal || canLegalAct || canDirectorAct || canPublish) && (
        <Card className="card-soft border-midnight/20">
          <CardHeader>
            <CardTitle className="text-base">Ações</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Label>Parecer/Comentário</Label>
            <Textarea rows={3} value={comment} onChange={(e) => setComment(e.target.value)}
              placeholder="Descreva sua manifestação ou parecer"
              data-testid="doc-action-comment" />
            <div className="flex flex-wrap gap-2">
              {canSubmitLegal && (
                <Button onClick={() => action("submit-legal")} className="bg-midnight hover:bg-ink text-white" data-testid="action-submit-legal">
                  <Send className="w-4 h-4 mr-2" /> Enviar ao Jurídico
                </Button>
              )}
              {canLegalAct && (
                <>
                  <Button onClick={() => action("legal-approve")} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="action-legal-approve">
                    <CheckCircle2 className="w-4 h-4 mr-2" /> Aprovar (Jurídico)
                  </Button>
                  <Button onClick={() => action("legal-request-changes", true)} variant="outline" data-testid="action-legal-changes">
                    <XCircle className="w-4 h-4 mr-2" /> Solicitar ajustes
                  </Button>
                </>
              )}
              {canDirectorAct && (
                <>
                  <Button onClick={() => action("director-approve")} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="action-director-approve">
                    <CheckCircle2 className="w-4 h-4 mr-2" /> Aprovar (Diretora)
                  </Button>
                  <Button onClick={() => action("director-request-changes", true)} variant="outline" data-testid="action-director-changes">
                    <XCircle className="w-4 h-4 mr-2" /> Solicitar ajustes
                  </Button>
                </>
              )}
              {canPublish && (
                <Button onClick={() => action("publish")} className="bg-gold text-ink hover:bg-gold-deep hover:text-white" data-testid="action-publish">
                  <Send className="w-4 h-4 mr-2" /> Publicar ao cliente
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Star ratings (JURIDICO + DIRETORA) */}
      {(user.role === ROLES.JURIDICO || user.role === ROLES.DIRETORA) && (
        <Card className="card-soft">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Star className="w-4 h-4 text-gold-deep" /> Avaliação do consultor (5 critérios)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <RatingForm
              docId={doc.id}
              initialValue={user.role === ROLES.JURIDICO ? doc.legal_rating : doc.director_rating}
              label={user.role === ROLES.JURIDICO ? "Sua avaliação (Controle Jurídico)" : "Sua avaliação (Diretora)"}
              onSaved={refresh}
            />
          </CardContent>
        </Card>
      )}

      {/* Show existing ratings */}
      {(doc.legal_rating || doc.director_rating) && (
        <Card className="card-soft">
          <CardHeader>
            <CardTitle className="text-base">Avaliações registradas</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {doc.legal_rating && (
              <RatingDisplay rating={doc.legal_rating} label={`Controle Jurídico — ${doc.legal_rating.rated_by_name}`} />
            )}
            {doc.director_rating && (
              <RatingDisplay rating={doc.director_rating} label={`Diretora — ${doc.director_rating.rated_by_name}`} />
            )}
          </CardContent>
        </Card>
      )}

      {/* Free comment box */}
      <Card className="card-soft">
        <CardHeader>
          <CardTitle className="text-base">Adicionar comentário ao documento</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <Textarea rows={3} value={freeComment} onChange={(e) => setFreeComment(e.target.value)}
            placeholder="Apontamentos sobre o documento e/ou desempenho do consultor responsável"
            data-testid="free-comment-input" />
          <div className="flex justify-end">
            <Button onClick={submitFreeComment} className="bg-midnight hover:bg-ink text-white" data-testid="add-comment-button">
              <MessageSquare className="w-4 h-4 mr-2" /> Registrar comentário
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Comments / Pareceres */}
      <Card className="card-soft">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><MessageSquare className="w-4 h-4" /> Pareceres e ações</CardTitle>
        </CardHeader>
        <CardContent>
          {doc.comments.length === 0 ? (
            <div className="text-sm text-slate-500 py-6 text-center">Sem manifestações registradas ainda.</div>
          ) : (
            <ul className="space-y-3">
              {doc.comments.slice().reverse().map((c, i) => (
                <li key={i} className="p-3 border border-slate-200 rounded-md">
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <span className="font-medium text-slate-900">{c.actor_name}</span>
                    <span>·</span>
                    <span>{c.actor_role}</span>
                    <span>·</span>
                    <span>{formatDateTimeBR(c.at)}</span>
                    <StatusBadge status={c.action} label={c.action} />
                  </div>
                  <div className="mt-1 text-sm text-slate-700">{c.comment}</div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
